/**
 * @override
 * Custom implementation of UgcBlock component for King Living
 * Overrides the autogenerated skeleton from @amblique/contentful
 *
 * Initializes Pixlee widget with lazy loading support.
 * Widget loads when scrolled into view (within 200px).
 *
 * @see https://developers.pixlee.com/docs/pixlee-widget-user-guide#lazy-loading
 */

import PropTypes from 'prop-types';

import {Box, Skeleton, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {usePixleeWidget} from '@amblique/king-living/app/hooks/use-pixlee-widget';

/**
 * UgcBlock component - Renders Pixlee UGC widget container with lazy loading
 *
 * @param {Object} props - Component props
 * @param {Contentful.UgcBlock} props.component - UgcBlock component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @param {boolean} [props.lazyLoad=true] - Enable lazy loading (default: true)
 * @returns {React.ReactElement|null}
 */
export const UgcBlock = ({
  component,
  isComplete = true,
  isFetching = false,
  lazyLoad = true,
  className,
  ...props
}) => {
  const {title, bannerId} = component || {};
  const isLoading = isFetching || !isComplete;

  const {isReady, containerRef, containerId} = usePixleeWidget(bannerId, {
    lazyLoad,
    rootMargin: '200px',
  });

  const styles = useMultiStyleConfig('contentful/ugc-block', {
    component,
    isComplete,
    isFetching,
    isReady,
  });

  if (!isLoading && !bannerId) {
    console.warn('UgcBlock: Missing required bannerId field');

    return null;
  }

  const mergedClassName = [
    'ugc-block',
    isLoading && 'ugc-block--loading',
    className,
  ].filter(Boolean).join(' ');

  return (
    <Box
      ref={containerRef}
      className={mergedClassName}
      data-banner-id={bannerId}
      data-lazy-load={lazyLoad}
      data-title={title}
      data-widget-ready={isReady}
      {...styles.root}
      {...props}
    >
      {isLoading && <Skeleton {...styles.skeleton} />}

      {/* Pixlee container must stay mounted for lazy init once the block becomes visible */}
      {bannerId && (
        <Box
          id={containerId}
          {...styles.container}
        />
      )}
    </Box>
  );
};

UgcBlock.displayName = 'UgcBlock';
UgcBlock.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  lazyLoad: PropTypes.bool,
  className: PropTypes.string,
};

export default UgcBlock;
